import { Editor, Element, Node, Path, Range, Text, Transforms } from "slate";
import { ReactEditor } from "slate-react";
export const removeLink = (editor, { element, location }) => {
    Transforms.unwrapNodes(editor, {
        at: element ? ReactEditor.findPath(editor, element) : location,
        match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === "link",
        split: true
    });
};
export const updateLink = ({ editor, text, element, href = element.href, openInNewTab = element.openInNewTab }) => {
    expandSelectionToLink(editor);
    editor.selection && Transforms.select(editor, editor.selection);
    Transforms.setNodes(editor, { href, openInNewTab }, { match: (n) => Element.isElement(n) && n.type === "link" });
    if (text && editor.selection) {
        Transforms.insertText(editor, text, { at: editor.selection.anchor.path });
    }
};
export const createLinkNode = ({ children, openInNewTab, href, text }) => ({
    type: "link",
    href,
    openInNewTab,
    children: children || [{ text }]
});
export const insertLink = ({ href, editor, text = "", openInNewTab }) => {
    // insert link
    if (!href) {
        return;
    }
    const link = createLinkNode({ href, text, openInNewTab });
    (() => {
        // NOTE: editor.selection must always be used, as the value changes. Caching { selection } = editor
        // will cause errors due to a stale selection being referenced
        if (!editor.selection) {
            // Insert the new link node at the bottom of the Editor when selection
            // is falsy
            Transforms.insertNodes(editor, { type: "paragraph", children: [link] }, { select: true });
            return;
        }
        Editor.unhangRange(editor, editor.selection);
        // Remove nested links
        removeLink(editor, { location: editor.selection });
        const [parentNode, parentPath] = editor.selection ? Editor.parent(editor, editor.selection.focus.path) : [];
        if (!Element.isElement(parentNode)) {
            // if parent is not an element, insert a new paragraph with a link", { parentNode
            Transforms.insertNodes(editor, { type: "paragraph", children: [link] }, { select: true });
            return;
        }
        if (Element.isElement(parentNode) && parentNode.type === "image") {
            // Wrap the image in link
            // Move cursor out of the link so typing can continue *outside* the link
            Transforms.wrapNodes(editor, link, { split: false, at: editor.selection });
            Transforms.collapse(editor, { edge: "end" });
            Transforms.move(editor, { distance: 2, edge: "start" });
            return;
        }
        if (parentPath && editor.isVoid(parentNode)) {
            // Insert the new link after the void node
            Transforms.insertNodes(editor, { type: "paragraph", children: [link] }, {
                at: Path.next(parentPath),
                select: true
            });
            return;
        }
        if (editor.selection && Range.isCollapsed(editor.selection)) {
            if (parentNode.type === "link") {
                // Update parentLink when cursor is inside a link
                expandSelectionToLink(editor);
                insertLink({ editor, openInNewTab, text, href });
                return;
            }
            // Insert the new link in our last known location
            Transforms.insertNodes(editor, [link, { text: "" }], { select: true });
            return;
        }
        if (text) {
            // replace selection with updated text
            Transforms.insertNodes(editor, link, { select: true });
            return;
        }
        Transforms.wrapNodes(editor, link, { split: true });
    })();
    // Remove the highlight and move the cursor to the end of the highlight
    Transforms.collapse(editor, { edge: "end" });
    // Move cursor out of the link so typing can continue *outside* the link
    Transforms.move(editor, { distance: 1, unit: "offset" });
};
export const wrapInLink = ({ editor, path, href, openInNewTab }) => {
    const link = createLinkNode({ href, openInNewTab, text: "" });
    Transforms.wrapNodes(editor, link, { at: path, split: true });
    // Move cursor outside of link so you can type
    Transforms.collapse(editor, { edge: "end" });
    Transforms.move(editor, { distance: 2, unit: "offset" });
};
export const getSelectedLink = (editor) => {
    const [selectedLink] = getParentLink(editor);
    // NOTE: editor.selection must always be used, as the value changes.
    // Caching { selection } = editor will cause errors due to a stale selection being referenced
    if (!editor.selection) {
        return { selectedText: false, selectedLink: null };
    }
    const selectedTexts = editor.selection &&
        Array.from(Node.texts(editor, { from: editor.selection.anchor.path, to: editor.selection.focus.path }) || []);
    const isOnlyText = selectedTexts?.length === 1;
    const selectedText = (() => {
        if (isOnlyText) {
            const { text } = selectedTexts[0][0];
            if (editor.selection && Range.isExpanded(editor.selection)) {
                const [start, end] = Range.edges(editor.selection);
                return text.slice(start.offset, end.offset);
            }
        }
        return "";
    })();
    if (selectedLink) {
        if (editor.selection && Range.isCollapsed(editor.selection)) {
            const selectedText = (selectedLink.children.length === 1 &&
                Text.isText(selectedLink.children[0]) &&
                selectedLink.children[0].text) ||
                false;
            return {
                selectedText,
                selectedLink
            };
        }
        return {
            selectedText,
            selectedLink
        };
    }
    return {
        selectedText: (isOnlyText ? selectedText : false),
        selectedLink: null
    };
};
export const isLinkActive = (editor) => {
    const { selection } = editor;
    if (!selection) {
        return false;
    }
    const [parent] = Editor.parent(editor, selection.focus?.path);
    return Element.isElement(parent) && parent.type === "link";
};
export const expandSelectionToLink = (editor) => {
    if (!editor.selection) {
        return;
    }
    const [node, nodePath] = Editor.node(editor, editor.selection.focus.path);
    if (Element.isElement(node) && node.type === "link") {
        // Node is link, expand to node
        Transforms.select(editor, nodePath);
        return;
    }
    // Parent is link, expand to parent
    const [link, linkPath] = Editor.above(editor, {
        at: editor.selection.focus.path,
        match: (n) => Element.isElement(n) && n.type === "link"
    }) || [];
    if (link && linkPath) {
        Transforms.select(editor, linkPath);
    }
    Editor.unhangRange(editor, editor.selection);
};
export const getParentLink = (editor, location) => {
    try {
        // Editor.unhangRange(editor, editor.selection);
        const parentLink = Editor.above(editor, {
            at: location,
            mode: "lowest",
            match: (n) => Element.isElement(n) && n.type === "link"
        }) || [];
        return parentLink;
    }
    catch (e) {
        // Slate throws if it can't find and element :shrug:, which it won't when a link is removed
        return [];
    }
};
